/**
 * The creature pack: what `pnpm extract` writes to generated/ and how the game reads it back.
 * One file per generation, so the client fetches only the gens a player filters to. Ability and
 * item translations are joined from balance.json at load time, so tuning one needs no re-extract.
 */
import type { AbilityEffect, Creature, ItemEffect, Move, Stats, TypeName } from '@tdmons/sim';
import balanceJson from '../balance.json';

type Balance = typeof balanceJson;

export const ALL_GENS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;
export type Gen = (typeof ALL_GENS)[number];

/** One species as stored: learnset entries are indexes into its file's `moves`. */
export interface PackSpecies {
  id: string;
  name: string;
  num: number;
  types: [TypeName] | [TypeName, TypeName];
  baseStats: Stats;
  weight: number;
  abilities: string[];
  learnset: number[];
}

/** generated/gen{N}.json */
export interface GenFile {
  gen: Gen;
  dexVersion: string;
  /** Every move this gen's species can learn, sorted by id. */
  moves: Move[];
  /** Ability id -> display name, for every ability this gen's species have. */
  abilities: Record<string, string>;
  /** Sorted by dex number. */
  species: PackSpecies[];
}

/** generated/manifest.json */
export interface PackManifest {
  dexVersion: string;
  gens: Record<string, { species: number; moves: number; bytes: number; noneEffects: number }>;
  totalBytes: number;
  /** Effect tag -> number of distinct moves, across every gen. */
  effects: Record<string, number>;
}

export interface CreaturePack {
  gens: Gen[];
  dexVersion: string;
  creatures: Creature[];
  moves: Map<string, Move>;
  /** Ability id -> display name. */
  abilityNames: Map<string, string>;
}

// One literal import per gen so Vite splits each into its own chunk.
const GEN_FILES: Record<Gen, () => Promise<{ default: unknown }>> = {
  1: () => import('../generated/gen1.json', { with: { type: 'json' } }),
  2: () => import('../generated/gen2.json', { with: { type: 'json' } }),
  3: () => import('../generated/gen3.json', { with: { type: 'json' } }),
  4: () => import('../generated/gen4.json', { with: { type: 'json' } }),
  5: () => import('../generated/gen5.json', { with: { type: 'json' } }),
  6: () => import('../generated/gen6.json', { with: { type: 'json' } }),
  7: () => import('../generated/gen7.json', { with: { type: 'json' } }),
  8: () => import('../generated/gen8.json', { with: { type: 'json' } }),
  9: () => import('../generated/gen9.json', { with: { type: 'json' } }),
};

export async function loadGenFile(gen: Gen): Promise<GenFile> {
  const load = GEN_FILES[gen];
  if (!load) throw new Error(`No generation ${String(gen)}; gens are 1-9`);
  try {
    return (await load()).default as GenFile;
  } catch (err) {
    throw new Error(
      `The creature pack for Gen ${gen} is missing. Run \`pnpm extract\` (it builds packages/data/generated/).`,
      { cause: err },
    );
  }
}

/** A stored species as the sim's Creature. */
export function toCreature(s: PackSpecies, file: Pick<GenFile, 'gen' | 'moves'>): Creature {
  return {
    id: s.id,
    name: s.name,
    gen: file.gen,
    types: s.types,
    baseStats: s.baseStats,
    weight: s.weight,
    learnset: s.learnset.map((i) => {
      const move = file.moves[i];
      if (!move) throw new Error(`${s.id}: learnset index ${i} is not a move in Gen ${file.gen}`);
      return move.id;
    }),
    abilities: s.abilities,
    spriteKey: s.id,
  };
}

/** Every creature, move, and ability name for the given gens (any subset of 1-9, any order). */
export async function loadPack(gens: readonly Gen[] = ALL_GENS): Promise<CreaturePack> {
  const wanted = [...new Set(gens)].sort((a, b) => a - b);
  const files = await Promise.all(wanted.map(loadGenFile));
  const creatures: Creature[] = [];
  const moves = new Map<string, Move>();
  const abilityNames = new Map<string, string>();
  for (const file of files) {
    for (const m of file.moves) moves.set(m.id, m);
    for (const [id, name] of Object.entries(file.abilities)) abilityNames.set(id, name);
    for (const s of file.species) creatures.push(toCreature(s, file));
  }
  return { gens: wanted, dexVersion: files[0]?.dexVersion ?? '', creatures, moves, abilityNames };
}

export async function loadCreatures(gens: readonly Gen[] = ALL_GENS): Promise<Creature[]> {
  return (await loadPack(gens)).creatures;
}

export async function loadMoves(gens: readonly Gen[] = ALL_GENS): Promise<Map<string, Move>> {
  return (await loadPack(gens)).moves;
}

/** The ability's football effect, or `none` for anything off the whitelist. */
export function abilityEffect(id: string, b: Balance = balanceJson): AbilityEffect {
  const entry = (b.abilities as Record<string, unknown>)[id];
  if (id === '$comment' || !entry) return { kind: 'none' };
  return entry as AbilityEffect;
}

/** The ids of the abilities that do something on the field. */
export function whitelistedAbilities(b: Balance = balanceJson): string[] {
  return Object.keys(b.abilities).filter((k) => k !== '$comment');
}

export interface ItemDef {
  id: string;
  name: string;
  slot: 'held' | 'bag';
  effect: ItemEffect;
}

/** Every item that maps across from the games; nothing else exists. */
export function items(b: Balance = balanceJson): ItemDef[] {
  return Object.entries(b.items)
    .filter(([k]) => k !== '$comment')
    .map(([id, v]) => ({ id, ...(v as Omit<ItemDef, 'id'>) }));
}
